import { drawExtensionIcon } from '@/extensions/pixelIcons';
import {
  clockedConfig,
  clockedRuntime,
  clockIo,
  clockModeItem,
  clockNode,
  clockedBuild,
  watchSampledInput,
} from '../clocked';
import { z } from 'zod';
import { reaction } from 'mobx';
import { configureTile } from '../configuration';
import type { DigitilesExtension } from '../catalog';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from '../activeNodes';
import { InputType, type TileDefinition } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';

export const MOUSE_TYPE = 'extension:digitiles.mouse/sensor' as const;
const modeSchema = z.enum(['primary', 'secondary', 'middle', 'inside', 'x', 'y']);
const configSchema = z.object({
  ...clockedConfig,
  mode: modeSchema,
  bit: z.number().int().min(0).max(7),
});
type Config = z.infer<typeof configSchema>;
const runtimeSchema = configSchema.extend({
  ...clockedRuntime,
  enabled: z.boolean(),
  value: z.boolean(),
});
type Runtime = z.infer<typeof runtimeSchema>;
const ioSides = {
  [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
  [Side.BOTTOM]: clockIo,
  [Side.LEFT]: { type: TileSideIOType.NONE, forceConnection: false },
};
const tile: TileDefinition<Config> = {
  type: MOUSE_TYPE,
  name: 'Mouse sensor',
  canRotate: true,
  stateSchema: configSchema,
  createDefaultState: () => ({ mode: 'primary', bit: 7, clocked: false }),
  graphConfiguration: (state) => ({
    mode: state.mode,
    bit: state.bit,
    clocked: state.clocked,
  }),
  ioSides,
  buildAndConnectNodes: clockedBuild(MOUSE_TYPE, false, true),
  createContextMenuItems: ({ instance, chipEditorModel }) => [
    clockModeItem({ instance, chipEditorModel }),
    {
      label: `Sensor mode (${instance.state.mode})`,
      action: async (takeInput) => {
        try {
          const mode = await takeInput(
            InputType.STRING,
            'Mouse mode: primary, secondary, middle, inside, x or y',
            instance.state.mode,
            true,
            (value) => ({
              isValid: modeSchema.safeParse(value).success,
              errorMessage: 'Choose primary, secondary, middle, inside, x or y.',
            }),
          );
          configureTile(
            instance,
            chipEditorModel,
            { mode: modeSchema.parse(mode) },
            'Change mouse sensor mode',
          );
        } catch {
          /* Cancel keeps the previous mode. */
        }
      },
    },
    {
      label: `Position bit (${instance.state.bit})`,
      action: async (takeInput) => {
        try {
          const bit = await takeInput(
            InputType.NUMBER,
            'Position bit (0–7; 7 is the high bit)',
            instance.state.bit,
            true,
            (value) => ({
              isValid: configSchema.shape.bit.safeParse(Number(value)).success,
              errorMessage: 'Enter a whole number from 0 to 7.',
            }),
          );
          configureTile(
            instance,
            chipEditorModel,
            { bit: configSchema.shape.bit.parse(Number(bit)) },
            'Change mouse position bit',
          );
        } catch {
          /* Cancel keeps the previous bit. */
        }
      },
    },
  ],
  graphics: {
    type: MOUSE_TYPE,
    draw(g, { meta, connections, context }) {
      defaultTileGraphics(g);
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        ioSides,
        context,
      );
      drawExtensionIcon(g, 'mouse', meta.outputs.right ? 0x8bd6ff : 0x8192a4);
    },
  },
};

export const mouseExtension: DigitilesExtension = {
  id: 'digitiles.mouse',
  name: 'Mouse input',
  version: '1.1.0',
  description:
    'Read pointer buttons, presence or position bits within the tile map. Use the tile menu to sample on a rising bottom clock. Input is limited to this page.',
  capabilities: ['mouse'],
  tiles: [{ definition: tile, circuits: [CircuitType.ChipLogic] }],
  nodes: [
    {
      type: MOUSE_TYPE,
      stateSchema: runtimeSchema.transform((state) => ({
        ...state,
        sampled: false,
        enabled: false,
        value: false,
      })),
      configurationKeys: ['mode', 'bit', 'enabled', 'clocked'],
      createDefaultState: (): Runtime => ({
        clocked: false,
        sampled: false,
        mode: 'primary',
        bit: 7,
        enabled: false,
        value: false,
      }),
      process: (_input, state: Runtime) =>
        state.enabled && (state.clocked ? state.sampled : state.value),
    },
    clockNode(MOUSE_TYPE),
  ],
  activate(app, signal) {
    let active: ActiveExtensionNode[] = [];
    let groups = new Map<string, ActiveExtensionNode[]>();
    let last = new Map<string, boolean>();
    let sample = { inside: false, buttons: 0, x: 0, y: 0 };
    let frame: number | undefined;
    const assign = (target: ActiveExtensionNode, enabled: boolean, value: boolean) => {
      const state = target.node.state as Runtime;
      if (state.enabled === enabled && state.value === value) return;
      const changedEnable = state.enabled !== enabled;
      state.enabled = enabled;
      if (!enabled) state.sampled = false;
      state.value = value;
      if (
        (!state.clocked || changedEnable) &&
        target.simulation.graph.nodes.get(target.node.id) === target.node
      )
        target.simulation.triggerUpdate(target.node.id);
    };
    const value = (mode: Config['mode'], bit: number) =>
      sample.inside &&
      (mode === 'inside' ||
        (mode === 'x' || mode === 'y'
          ? ((sample[mode] >> bit) & 1) !== 0
          : (sample.buttons & { primary: 1, secondary: 2, middle: 4 }[mode]) !== 0));
    const stopClock = watchSampledInput(app, MOUSE_TYPE, (target) =>
      value(target.node.state.mode, target.node.state.bit),
    );
    const flush = () => {
      frame = undefined;
      for (const [key, nodes] of groups) {
        const state = nodes[0].node.state as Runtime;
        const next = value(state.mode, state.bit);
        if (last.get(key) === next) continue;
        last.set(key, next);
        for (const target of nodes) assign(target, true, next);
      }
    };
    const cancelFrame = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = undefined;
    };
    const reset = () => {
      cancelFrame();
      sample = { inside: false, buttons: 0, x: 0, y: 0 };
      flush();
    };
    const unwatch = watchActiveExtensionNodes(app, MOUSE_TYPE, (nodes) => {
      cancelFrame();
      const retained = new Set(nodes.map((target) => target.node));
      for (const target of active)
        if (!retained.has(target.node)) assign(target, false, false);
      active = nodes;
      groups = new Map();
      last = new Map();
      for (const target of active) {
        const state = target.node.state as Runtime;
        const key = `${state.mode}:${state.mode === 'x' || state.mode === 'y' ? state.bit : 0}`;
        let group = groups.get(key);
        if (!group) groups.set(key, (group = []));
        group.push(target);
      }
      flush();
    });
    const mapFor = (target: EventTarget | null) =>
      target instanceof Element ? target.closest('#tile-map') : null;
    const update = (event: PointerEvent) => {
      const map = mapFor(event.target);
      if (
        !map ||
        app.uiModel.inDialog ||
        document.hidden ||
        event.pointerType === 'touch'
      ) {
        reset();
        return;
      }
      const rect = map.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        reset();
        return;
      }
      const byte = (n: number) => Math.max(0, Math.min(255, Math.floor(n * 256)));
      sample = {
        inside: true,
        buttons: event.buttons,
        x: byte((event.clientX - rect.left) / rect.width),
        y: byte((event.clientY - rect.top) / rect.height),
      };
      if (event.type === 'pointermove') {
        if (frame === undefined) frame = requestAnimationFrame(flush);
      } else {
        cancelFrame();
        flush();
      }
    };
    for (const type of ['pointermove', 'pointerdown', 'pointerup'] as const)
      window.addEventListener(type, update, { signal, passive: true });
    window.addEventListener(
      'pointerout',
      (event) => {
        if (mapFor(event.target) && !mapFor(event.relatedTarget)) reset();
      },
      { signal, passive: true },
    );
    window.addEventListener('pointercancel', reset, { signal });
    window.addEventListener('blur', reset, { signal });
    document.addEventListener('visibilitychange', reset, { signal });
    const stopDialogWatch = reaction(
      () => app.uiModel.inDialog,
      (open) => {
        if (open) reset();
      },
    );
    return () => {
      cancelFrame();
      stopDialogWatch();
      sample = { inside: false, buttons: 0, x: 0, y: 0 };
      stopClock();
      unwatch();
    };
  },
};
